import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, X, Copy, Gamepad2, FileText, Bell, Zap, Crosshair } from "lucide-react";
import { hunterTier } from "../lib/constants";

const HANDLE_PLATFORMS = ["PSN", "Xbox", "Steam", "PC/Epic", "Other"];

export default function MyReports() {
  const { user, loading: authLoading, setUser } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [handles, setHandles] = useState([]);
  const [newHandle, setNewHandle] = useState({ platform: "PSN", tag: "" });
  const [savingHandles, setSavingHandles] = useState(false);
  const [prefs, setPrefs] = useState({ notify_bell: true, notify_email: true });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (!user) return;
    setHandles(user.linked_handles || []);
    setPrefs({ notify_bell: user.notify_bell !== false, notify_email: user.notify_email !== false });
    api.get("/my/reports").then(({ data }) => setReports(data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (!authLoading && !user) return <Navigate to="/" replace />;
  if (authLoading || !user) return <div className="flex justify-center py-32 text-[#64748B]"><Loader2 className="animate-spin" /></div>;

  const addHandle = () => {
    if (!newHandle.tag.trim()) return;
    setHandles([...handles, { platform: newHandle.platform, tag: newHandle.tag.trim() }]);
    setNewHandle({ platform: newHandle.platform, tag: "" });
  };

  const saveHandles = async () => {
    setSavingHandles(true);
    try {
      const { data } = await api.put("/users/me/handles", { handles });
      setUser(data);
      toast.success("Your gaming handles were saved.");
    } catch { toast.error("Could not save handles."); }
    finally { setSavingHandles(false); }
  };

  const copyId = (id) => { navigator.clipboard.writeText(id); toast.success("Report ID copied — share it to track status."); };

  const boost = async (report) => {
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "report_boost", origin_url: window.location.origin, report_id: report.id });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start boost checkout."); }
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    setSavingPrefs(true);
    try {
      const { data } = await api.put("/users/me/prefs", next);
      setUser(data);
    } catch { toast.error("Could not update preferences."); }
    finally { setSavingPrefs(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <h1 className="animate-fade-up font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">My Reports & Profile</h1>
      <p className="mt-2 text-[#94A3B8]">Track your submissions and link your gamertags so the community knows who you are.</p>

      {user.confirmed_busts > 0 && (() => { const t = hunterTier(user.confirmed_busts); return (
        <div data-testid="hunter-banner" className="animate-fade-up mt-5 flex flex-wrap items-center gap-3 rounded-2xl border p-4" style={{ borderColor: `${t.color}4D`, backgroundColor: `${t.color}12` }}>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${t.color}26`, color: t.color }}>
            <Crosshair size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 font-heading text-base font-bold text-white">
              {t.label}
              <span className="rounded px-1.5 py-0.5 text-[11px] font-bold uppercase" style={{ color: t.color, backgroundColor: `${t.color}26` }}>{user.confirmed_busts} confirmed</span>
            </div>
            <p className="text-sm text-[#94A3B8]">
              {user.confirmed_busts === 1
                ? "One of your call-outs was confirmed by our mod team. Keep hunting!"
                : `${user.confirmed_busts} of your call-outs have been confirmed by our mod team. Elite work.`}
            </p>
          </div>
          <Button data-testid="view-hunter-profile-btn" onClick={() => navigate(`/hunters/${user.user_id}`)} variant="outline" size="sm" className="border-[#1E293F] bg-[#141B2D] text-white hover:bg-[#1A233A] hover:text-white">
            View public profile
          </Button>
        </div>
      ); })()}

      {/* Linked handles */}
      <div className="animate-fade-up mt-8 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-white"><Gamepad2 size={18} className="text-[#00F0FF]" /> Your Gaming Handles</h2>
        <p className="mt-1 text-sm text-[#94A3B8]">Add your PSN / Xbox / Steam IDs so people can recognize your reports.</p>

        <div className="mt-4 flex flex-wrap gap-2">
          {handles.map((h, i) => (
            <span key={i} data-testid={`handle-${i}`} className="inline-flex items-center gap-2 rounded-full border border-[#1E293F] bg-[#141B2D] px-3 py-1.5 text-sm text-white">
              <span className="text-xs font-bold text-[#00F0FF]">{h.platform}</span> {h.tag}
              <button onClick={() => setHandles(handles.filter((_, idx) => idx !== i))} className="text-[#64748B] hover:text-[#FF2A55]"><X size={13} /></button>
            </span>
          ))}
          {handles.length === 0 && <span className="text-sm text-[#64748B]">No handles linked yet.</span>}
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Select value={newHandle.platform} onValueChange={(v) => setNewHandle({ ...newHandle, platform: v })}>
            <SelectTrigger data-testid="handle-platform-select" className="w-full border-[#1E293F] bg-[#141B2D] text-white sm:w-36"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[#1E293F] bg-[#141B2D] text-white">
              {HANDLE_PLATFORMS.map((p) => <SelectItem key={p} value={p} className="focus:bg-[#1A233A] focus:text-white">{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="handle-tag-input" value={newHandle.tag} onChange={(e) => setNewHandle({ ...newHandle, tag: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addHandle()} placeholder="Your gamertag / ID" className="flex-1 border-[#1E293F] bg-[#141B2D] text-white" />
          <Button data-testid="add-handle-btn" onClick={addHandle} variant="outline" className="border-[#1E293F] bg-[#141B2D] text-white hover:bg-[#1A233A] hover:text-white"><Plus size={15} className="mr-1" /> Add</Button>
        </div>
        <Button data-testid="save-handles-btn" onClick={saveHandles} disabled={savingHandles} className="mt-3 bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">
          {savingHandles && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Handles
        </Button>
      </div>

      {/* Alert preferences */}
      <div className="animate-fade-up mt-8 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6" data-testid="alert-prefs">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-white"><Bell size={18} className="text-[#00F0FF]" /> Alert Preferences</h2>
        <p className="mt-1 text-sm text-[#94A3B8]">Choose how you're notified when a player you reported or faced gets banned.</p>
        <div className="mt-4 space-y-3">
          <label className="flex items-center justify-between rounded-lg border border-[#1E293F] bg-[#141B2D] px-4 py-3">
            <span>
              <span className="block text-sm font-semibold text-white">In-app bell</span>
              <span className="block text-xs text-[#64748B]">Show alerts in the notification bell.</span>
            </span>
            <Switch data-testid="pref-bell" checked={prefs.notify_bell} disabled={savingPrefs} onCheckedChange={(v) => savePrefs({ ...prefs, notify_bell: v })} />
          </label>
          <label className="flex items-center justify-between rounded-lg border border-[#1E293F] bg-[#141B2D] px-4 py-3">
            <span>
              <span className="block text-sm font-semibold text-white">Email</span>
              <span className="block text-xs text-[#64748B]">Email me when there's an update (active once email is enabled).</span>
            </span>
            <Switch data-testid="pref-email" checked={prefs.notify_email} disabled={savingPrefs} onCheckedChange={(v) => savePrefs({ ...prefs, notify_email: v })} />
          </label>
        </div>
        <p className="mt-3 text-xs text-[#64748B]">Turn both off for "none". Your changes save automatically.</p>
      </div>

      {/* My reports */}
      <div className="animate-fade-up mt-8">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-white"><FileText size={18} className="text-[#00F0FF]" /> My Submitted Reports</h2>
        {loading ? (
          <div className="flex justify-center py-12 text-[#64748B]"><Loader2 className="animate-spin" /></div>
        ) : reports.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-[#1E293F] bg-[#0F1424] py-12 text-center text-[#94A3B8]" data-testid="empty-my-reports">
            You haven't reported anyone yet. <button onClick={() => navigate("/submit")} className="font-semibold text-[#00F0FF] hover:underline">Report a cheater</button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {reports.map((r) => (
              <div key={r.id} data-testid={`my-report-${r.id}`} className="rounded-xl border border-[#1E293F] bg-[#0F1424] p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <button onClick={() => navigate(`/players/${r.platform}/${encodeURIComponent(r.gamertag)}`)} className="font-heading text-lg font-bold text-white hover:text-[#00F0FF]">{r.gamertag}</button>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-[#94A3B8]">
                      <span className="text-[#00F0FF]">{r.platform}</span> · <span>{r.game_mode}</span> · <span className="text-[#FF2A55]">{r.cheat_type}</span>
                    </div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#64748B]">
                  <span className="font-mono">ID: {r.id.slice(0, 8)}…</span>
                  <button data-testid={`copy-id-${r.id}`} onClick={() => copyId(r.id)} className="inline-flex items-center gap-1 text-[#00F0FF] hover:underline"><Copy size={12} /> Copy tracking ID</button>
                  {r.status === "pending" && !r.boosted && (
                    <button data-testid={`boost-${r.id}`} onClick={() => boost(r)} className="ml-auto inline-flex items-center gap-1 rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10 px-2.5 py-1 font-semibold text-[#FFB800] hover:bg-[#FFB800]/20">
                      <Zap size={12} /> Boost to top · $3
                    </button>
                  )}
                  {r.boosted && <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#FFB800]/15 px-2.5 py-1 font-semibold text-[#FFB800]"><Zap size={12} /> Boosted</span>}
                </div>
                {r.mod_note && (
                  <div data-testid={`mod-note-${r.id}`} className="mt-3 rounded-lg border border-[#1E293F] bg-[#0B1120] px-3 py-2 text-sm">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Modder note</span>
                    <p className="mt-0.5 text-[#CBD5E1]">{r.mod_note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
